import { Box, Loader, Group, Menu } from '@mantine/core';
import { Button } from 'src/design-system/Button';
import { CollapsibleSearch } from 'src/design-system/CollapsibleSearch';
import { FamilyIndividualToggle } from 'src/non-financial-data/v2/FamilyIndividualToggle';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import classes from './ClientsPage.module.css';
import { IconChevronDown } from '@tabler/icons-react';
import { NewClientsTable } from './NewClientsTable';
import { FamiliesTable } from './FamiliesTable';
import { ClientTabs, useClientTab } from './hooks/useClientTab';
import { useNavigate, useSearchParams } from 'react-router';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClientsContext } from 'src/clients/context/ClientsContext';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSessionStorage } from '@mantine/hooks';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { useClientsPaginated } from './hooks/useClientsPaginated';
import { ClientsEmptyState } from './components/ClientsEmptyState';
import { useFlags } from 'src/feature-flags/FlagsHook';

export default function ClientsPage() {
  const { currentTab, setTab } = useClientTab();
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();
  const { startClientCreation } = useClientsContext();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const handledCreate = useRef(false);
  const { flagEnabledSiestaSteering } = useFlags();
  const { setSidebarFullScreen, setSidebarContent, setPreventSidebarCollapse } =
    useAppContainerContext();

  const clientsFilter = getClientsFilter(currentUser);
  const paginatedClients = useClientsPaginated(clientsFilter);
  const clientsExist = paginatedClients.total > 0;
  const isClientsLoading = paginatedClients.isLoading;
  const [emptyStateDismissed, setEmptyStateDismissed] = useSessionStorage({
    key: 'clients-empty-state-dismissed',
    defaultValue: false,
  });

  const showEmptyState =
    flagEnabledSiestaSteering && !isClientsLoading && !clientsExist && !emptyStateDismissed;

  const dismissEmptyState = () => {
    setEmptyStateDismissed(true);
    setSidebarFullScreen(false);
    setSidebarContent(null);
  };

  const handleAddManually = () => {
    dismissEmptyState();
    startClientCreation();
  };

  useLayoutEffect(() => {
    if (!showEmptyState) return;

    setSidebarFullScreen(true);
    setPreventSidebarCollapse(true);
    setSidebarContent(
      <ClientsEmptyState onAddManually={handleAddManually} onDismiss={dismissEmptyState} />,
    );

    return () => {
      setSidebarFullScreen(false);
      setPreventSidebarCollapse(false);
      setSidebarContent(null);
    };
  }, [showEmptyState]);

  useEffect(() => {
    if (searchParams.get('create') === 'true' && !handledCreate.current) {
      handledCreate.current = true;
      const firstName = searchParams.get('first_name') ?? undefined;
      const lastName = searchParams.get('last_name') ?? undefined;
      const emailAddress = searchParams.get('email_address') ?? undefined;
      const prefill =
        firstName || lastName || emailAddress ? { firstName, lastName, emailAddress } : undefined;
      startClientCreation(prefill);
      setSearchParams(
        prev => {
          prev.delete('create');
          prev.delete('first_name');
          prev.delete('last_name');
          prev.delete('email_address');
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, startClientCreation, setSearchParams]);

  const tabContent = getTabContent(currentUser, currentTab, searchTerm);

  if (isCurrentUserLoading) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box className={classes.familyFlowContent}>
      <Group justify="space-between" mb="md" align="center">
        <Group gap="sm" align="center" style={{ flex: searchOpen ? 1 : undefined }}>
          <CollapsibleSearch
            open={searchOpen}
            onOpenChange={setSearchOpen}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search..."
          >
            <FamilyIndividualToggle
              value={
                (currentTab ?? ClientTabs.Families) === ClientTabs.Families
                  ? 'family'
                  : 'individual'
              }
              onChange={view =>
                setTab(view === 'family' ? ClientTabs.Families : ClientTabs.Clients)
              }
              familyLabel="Families"
              individualLabel="Individuals"
              chosenSegmentPaddingX={{ left: 41.5, right: 37.5 }}
            />
          </CollapsibleSearch>
        </Group>
        {!searchOpen && (
          <Menu
            position="bottom-end"
            offset={8}
            width="target"
            styles={{
              dropdown: {
                borderRadius: 16,
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.8)',
                overflow: 'hidden',
                padding: 0,
              },
            }}
          >
            <Menu.Target>
              <Button
                variant="primary"
                px={undefined}
                pl={16}
                pr={12}
                rightSection={<IconChevronDown size={24} color="white" />}
                styles={{
                  root: {
                    '&:active': {
                      backgroundColor: 'var(--mantine-color-brand-8)',
                    },
                  },
                  section: { marginInlineStart: 8 },
                }}
              >
                Add family or individual
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                fz="sm"
                fw={FONT_WEIGHTS.medium}
                c="black"
                onClick={() => navigate('/clients/families/new')}
              >
                Add family
              </Menu.Item>
              <Menu.Item
                fz="sm"
                fw={FONT_WEIGHTS.medium}
                c="black"
                onClick={() => startClientCreation()}
              >
                Add individual
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      {tabContent}
    </Box>
  );
}

export function getClientsFilter(currentUser: { id?: string; userRole: string } | undefined): {
  advisorUserId?: string;
} {
  if (!currentUser?.id || ['SYSTEM_ADMIN', 'SERVICE_ADMIN'].includes(currentUser.userRole)) {
    return {};
  }
  return { advisorUserId: currentUser.id };
}

function getTabContent(
  currentUser: { id?: string; userRole: string } | undefined,
  currentTab: string | undefined,
  searchTerm?: string,
) {
  if (!currentUser?.id) return null;

  const filters = getClientsFilter(currentUser);

  if (currentTab === ClientTabs.Clients) {
    return <NewClientsTable filters={filters} searchTerm={searchTerm} />;
  }
  if (currentTab === ClientTabs.Families) {
    return <FamiliesTable filters={filters} searchTerm={searchTerm} />;
  }
  return null;
}
